/**
 * WhatChangedTile — home-page auto-narrative.
 *
 * Scans the current user's pinned hero tiles, picks the single
 * biggest gainer and biggest decliner (by |delta|), and renders a
 * one-sentence callout designed to be copy/pasted straight into a
 * CFO email. Empty state: a gentle hint to pin tiles from the
 * health dashboards.
 *
 * Data: GET /api/quarterly-deck/pins/movers (auth required).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowUpRight, ArrowDownRight, Copy, Check, Loader2, Lightbulb, Clock, CheckCircle2, FileDown, Share2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import logger from '../../lib/logger';
import { TileInfoButton } from './TileInfoButton';

export function WhatChangedTile() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [creatingShare, setCreatingShare] = useState(false);
  // Local optimistic mute — once Snooze/Mark Acted is fired we
  // hide the suggestion immediately rather than waiting for the
  // next /movers refetch (the muted state will persist after a
  // hard reload because the backend honors it).
  const [suggestionMuted, setSuggestionMuted] = useState(null); // 'snooze' | 'acted' | null
  const [suggestionBusy, setSuggestionBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/quarterly-deck/pins/movers?lookback_days=90');
        if (!cancelled) setData(res.data);
      } catch (err) {
        logger.error?.('movers fetch failed', err);
        if (!cancelled) setData({ headline: null, pin_count_scanned: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-testid="what-changed-tile-loading">
        <CardContent className="py-6 flex items-center gap-3 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Scanning your pinned tiles…</span>
        </CardContent>
      </Card>
    );
  }

  // Empty state — encourage pinning.
  if (!data?.headline) {
    return (
      <Card
        className="border-dashed border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white"
        data-testid="what-changed-tile-empty"
      >
        <CardContent className="py-5">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-slate-100 p-2 mt-0.5">
              <Sparkles className="h-4 w-4 text-slate-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-700">What changed this quarter?</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Pin a hero tile from any health dashboard (📌 button, top-right) and we'll
                automatically write a one-sentence movement callout here — ready to paste
                into your next CFO email.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { biggest_gainer: gain, biggest_decliner: dec, headline, suggested_action: suggestion, is_snapshot: isSnapshot, recent_pins: recentPins } = data;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(headline);
      setCopied(true);
      toast.success('Headline copied');
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  // Build a CFO-ready PDF from every pinned tile and trigger a
  // browser download. Endpoint streams `application/pdf` so we
  // request as `blob` and feed an anchor click. Filename comes
  // from the server's Content-Disposition.
  const handleDownloadPdf = async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const res = await api.get('/quarterly-deck/pins/export-pdf', {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Pull suggested filename out of Content-Disposition; fall
      // back to a sensible default so the download still works
      // when the header is stripped by a proxy.
      const cd = res.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : 'Quarterly_Impact_Deck.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Quarterly Deck downloaded');
    } catch (err) {
      logger.error?.('quarterly deck download failed', err);
      const detail = err?.response?.data?.detail
        || (err?.response?.status === 404
            ? 'Pin a hero tile first — there\'s nothing to export yet.'
            : 'Could not generate PDF — try again.');
      toast.error(detail);
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Mint a public share link and copy it to the clipboard. The
  // recipient lands on `/q/<token>` (a public, unauthenticated
  // page) and clicks Download; every open is recorded against the
  // token so the owner can later see "CFO opened it twice".
  const handleCreateShare = async () => {
    if (creatingShare) return;
    setCreatingShare(true);
    try {
      const res = await api.post('/quarterly-deck/pins/share', {
        expires_in_days: 60,
      });
      const url = res.data?.share_url;
      if (!url) {
        toast.error('Share link could not be created');
        return;
      }
      // Resolve to absolute URL — backend returns `/q/<token>`
      // when no PUBLIC_APP_URL is set, but recipients need a
      // copy-pasteable URL with origin.
      const absolute = url.startsWith('http')
        ? url
        : `${window.location.origin}${url}`;
      try {
        await navigator.clipboard.writeText(absolute);
        toast.success('Share link copied to clipboard', {
          description: 'Anyone with this link can view the deck for 60 days.',
        });
      } catch {
        // Clipboard blocked (rare) — surface the URL inline so the
        // user can copy manually rather than losing the work.
        toast.success('Share link created', { description: absolute });
      }
    } catch (err) {
      logger.error?.('quarterly deck share failed', err);
      const detail = err?.response?.data?.detail || 'Could not create share link';
      toast.error(detail);
    } finally {
      setCreatingShare(false);
    }
  };

  const goTo = (path) => {
    if (!path) return;
    navigate(path);
  };

  // Build the payload echoed back to the snooze/acted endpoints
  // so the backend can record the value at action time for the
  // "Manager Coaching v2 fix lifted Avg ROI +0.3x" celebration
  // we'll wire next quarter.
  const sendSuggestionAction = async (kind) => {
    if (!suggestion?.key || suggestionBusy) return;
    setSuggestionBusy(true);
    try {
      await api.post(`/quarterly-deck/pins/suggestions/${kind}`, {
        key: suggestion.key,
        label: dec?.label || null,
        value_at_action: dec?.value || null,
        decliner_path: dec?.pin_path || null,
      });
      setSuggestionMuted(kind);
      toast.success(kind === 'snooze' ? 'Snoozed for 7 days' : 'Marked as acted');
    } catch (err) {
      logger.error?.('suggestion action failed', err);
      toast.error('Could not save — try again');
    } finally {
      setSuggestionBusy(false);
    }
  };

  const showSuggestion = suggestion && !suggestionMuted;

  return (
    <Card
      className="border-0 shadow-md bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white overflow-hidden relative"
      data-testid="what-changed-tile"
    >
      <div
        className="absolute inset-0 opacity-15 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 15% 30%, #ffffff 0%, transparent 35%), ' +
            'radial-gradient(circle at 90% 70%, #ffffff 0%, transparent 35%)',
        }}
        aria-hidden="true"
      />
      <CardContent className="py-5 relative">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-white/15 p-2 mt-0.5 backdrop-blur">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/80">
                What Changed This Quarter?
              </p>
              <TileInfoButton
                testid="what-changed-info"
                title="What Changed This Quarter?"
                body="Auto-summary of the single biggest mover (good) and single biggest mover (bad) across every hero tile you've pinned in the last 90 days. The headline is composed verbatim from your live dashboard data so you can paste it straight into a CFO email or board update without re-checking the underlying numbers."
                tone="dark"
              />
            </div>
            <p className="text-sm md:text-base font-medium mt-1.5 leading-snug" data-testid="what-changed-headline">
              {headline}
            </p>
            {showSuggestion && (
              <div
                className="mt-2.5 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-300/15 border border-amber-200/30"
                data-testid="what-changed-suggestion"
              >
                <Lightbulb className="h-4 w-4 text-amber-200 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p
                    className="text-xs md:text-sm leading-snug text-white"
                    data-testid="what-changed-suggestion-text"
                  >
                    <span className="font-semibold uppercase tracking-wider text-[10px] text-amber-200 mr-1.5">
                      Suggested Next Step
                    </span>
                    {suggestion.text}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5">
                    {suggestion.cta_label && suggestion.cta_path && (
                      <button
                        type="button"
                        onClick={() => goTo(suggestion.cta_path)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-amber-100 hover:text-white underline-offset-2 hover:underline transition-colors"
                        data-testid="what-changed-suggestion-cta"
                      >
                        {suggestion.cta_label}
                        <ArrowUpRight className="h-3 w-3" />
                      </button>
                    )}
                    <span className="text-amber-200/40">·</span>
                    <button
                      type="button"
                      onClick={() => sendSuggestionAction('snooze')}
                      disabled={suggestionBusy}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-100/80 hover:text-white transition-colors disabled:opacity-50"
                      data-testid="what-changed-suggestion-snooze"
                      title="Hide this suggestion for 7 days"
                    >
                      <Clock className="h-3 w-3" />
                      Snooze 7d
                    </button>
                    <button
                      type="button"
                      onClick={() => sendSuggestionAction('acted')}
                      disabled={suggestionBusy}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-100/80 hover:text-white transition-colors disabled:opacity-50"
                      data-testid="what-changed-suggestion-acted"
                      title="I've taken action on this — track outcome next quarter"
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      Mark Acted
                    </button>
                  </div>
                </div>
              </div>
            )}
            {suggestionMuted && (
              <p
                className="mt-2 text-[11px] text-white/60 italic"
                data-testid="what-changed-suggestion-muted"
              >
                {suggestionMuted === 'snooze'
                  ? 'Suggestion snoozed for 7 days.'
                  : 'Action recorded — we\'ll celebrate the result next quarter.'}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {gain && (
                <button
                  type="button"
                  onClick={() => goTo(gain.pin_path)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-400/25 hover:bg-emerald-400/40 transition-colors text-xs font-semibold border border-emerald-200/30"
                  data-testid="what-changed-gainer-chip"
                >
                  <ArrowUpRight className="h-3 w-3" />
                  {gain.label}: {gain.delta_text}
                </button>
              )}
              {dec && (
                <button
                  type="button"
                  onClick={() => goTo(dec.pin_path)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-rose-400/25 hover:bg-rose-400/40 transition-colors text-xs font-semibold border border-rose-200/30"
                  data-testid="what-changed-decliner-chip"
                >
                  <ArrowDownRight className="h-3 w-3" />
                  {dec.label}: {dec.delta_text}
                </button>
              )}
              {Array.isArray(recentPins) && recentPins.map((p) => (
                <button
                  key={p.pin_id || p.pin_title}
                  type="button"
                  onClick={() => goTo(p.pin_path)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 transition-colors text-xs font-semibold border border-white/25"
                  data-testid={isSnapshot ? "what-changed-snapshot-chip" : "what-changed-recent-chip"}
                  title={`Recently pinned: ${p.pin_title}`}
                >
                  <Sparkles className="h-3 w-3" />
                  {p.pin_title}{p.value ? ` · ${p.label}: ${p.value}` : ''}
                </button>
              ))}
              <span className="text-[11px] text-white/60 ml-1">
                from {data.pin_count_scanned} pinned tile{data.pin_count_scanned === 1 ? '' : 's'}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-stretch gap-2 shrink-0">
            <Button
              onClick={handleCopy}
              variant="outline"
              size="sm"
              className="bg-white/15 border-white/30 text-white hover:bg-white/25"
              data-testid="what-changed-copy-btn"
            >
              {copied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              variant="outline"
              size="sm"
              className="bg-white/15 border-white/30 text-white hover:bg-white/25"
              data-testid="what-changed-download-deck-btn"
              title="Build a CFO-ready PDF deck from every pinned tile"
            >
              {downloadingPdf ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <FileDown className="h-3.5 w-3.5 mr-1.5" />
              )}
              {downloadingPdf ? 'Generating…' : 'Download Deck'}
            </Button>
            <Button
              onClick={handleCreateShare}
              disabled={creatingShare}
              variant="outline"
              size="sm"
              className="bg-white/15 border-white/30 text-white hover:bg-white/25"
              data-testid="what-changed-share-deck-btn"
              title="Get a public link executives can open without logging in"
            >
              {creatingShare ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Share2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              {creatingShare ? 'Creating…' : 'Share Link'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default WhatChangedTile;
